import { format, parseISO } from 'date-fns'
import { Bug, Calendar, CalendarRange, CalendarX, ExternalLink, Repeat } from 'lucide-react'

import type { RegionId, Subscription } from '@snootsvet/shared'
import { formatCurrency, formatInterval, TERMINATED_SUBSCRIPTION_STATUSES } from '@snootsvet/shared'

import { Badge } from '@/frontend/components/ui/badge'

/** Read-only status, price, and billing dates for a patient's primary membership. */
export function PatientMembershipPrimaryDetails({
  subscription,
  pendingUpgradeInvoiceUrl,
}: {
  subscription: Subscription
  pendingUpgradeInvoiceUrl?: string
}) {
  const startDate = subscription.effectiveStartDate
    ? parseISO(subscription.effectiveStartDate)
    : subscription.startDate
      ? new Date(subscription.startDate)
      : null
  const membershipPrice = [
    subscription.interval
      ? formatInterval(subscription.interval, subscription.intervalCount)
      : null,
    subscription.amount != null && subscription.currency
      ? formatCurrency(subscription.amount, subscription.currency)
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="space-y-1.5 text-sm">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <StatusDot status={subscription.status} />
        <span className="font-medium text-foreground">
          Membership · {formatSubscriptionStatus(subscription.status)}
        </span>
        {membershipPrice && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Repeat className="size-3 shrink-0" />
            {membershipPrice}
          </span>
        )}
        {subscription.planType === 'remote' && (
          <Badge variant="secondary" size="sm">
            Remote
          </Badge>
        )}
        {subscription.pendingUpdate &&
          (pendingUpgradeInvoiceUrl ? (
            <a href={pendingUpgradeInvoiceUrl} target="_blank" rel="noopener noreferrer">
              <Badge variant="pending" size="sm">
                Pending upgrade
                <ExternalLink className="size-3" />
              </Badge>
            </a>
          ) : (
            <Badge variant="pending" size="sm">
              Pending upgrade
            </Badge>
          ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {startDate && (
          <span className="flex items-center gap-1">
            <Calendar className="size-3 shrink-0" />
            Member since {format(startDate, 'd MMM yyyy')}
          </span>
        )}
        {subscription.currentPeriodStart && subscription.currentPeriodEnd && (
          <span className="flex items-center gap-1">
            <CalendarRange className="size-3 shrink-0" />
            {format(new Date(subscription.currentPeriodStart), 'd MMM yyyy')} -{' '}
            {format(new Date(subscription.currentPeriodEnd), 'd MMM yyyy')}
          </span>
        )}
        {subscription.endedAt && (
          <span className="flex items-center gap-1 font-medium text-destructive-fg">
            <CalendarX className="size-3 shrink-0" aria-hidden />
            Ended {format(new Date(subscription.endedAt), 'd MMM yyyy')}
          </span>
        )}
        {subscription.cancelAt && !TERMINATED_SUBSCRIPTION_STATUSES.has(subscription.status) && (
          <span className="flex items-center gap-1 font-medium text-destructive-fg">
            <CalendarX className="size-3 shrink-0" aria-hidden />
            Cancels {format(new Date(subscription.cancelAt), 'd MMM yyyy')}
          </span>
        )}
      </div>
    </div>
  )
}

export function PatientMembershipAddons({ subscription }: { subscription: Subscription }) {
  if (subscription.addons.length === 0) return null

  return (
    <div className="space-y-3">
      {subscription.addons.map((addon) => (
        <div key={addon.priceId} className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <StatusDot status={subscription.status} />
          <span className="font-medium text-foreground text-sm">{addon.name}</span>
          <span className="flex items-center gap-1 text-muted-foreground">
            <Repeat className="size-3 shrink-0" />
            {formatInterval(addon.interval, addon.intervalCount)} ·{' '}
            {formatCurrency(addon.amount, addon.currency)}
          </span>
        </div>
      ))}
    </div>
  )
}

export function FleaWormingPlanBadge({
  subscription,
  memberRegion,
}: {
  subscription: Pick<Subscription, 'hasFleaWormTickPlan'>
  memberRegion: RegionId
}) {
  if (!subscription.hasFleaWormTickPlan) return null

  return (
    <Badge variant="success" size="sm">
      <Bug className="size-3" />
      {memberRegion === 'us' ? 'Flea & tick included' : 'Flea & worming included'}
    </Badge>
  )
}

function StatusDot({ status }: { status: Subscription['status'] }) {
  const colorClass =
    status === 'active'
      ? 'bg-success-fg'
      : status === 'past_due'
        ? 'bg-warning-fg'
        : 'bg-destructive-fg'
  return <span className={`inline-block size-1.5 shrink-0 rounded-full ${colorClass}`} />
}

function formatSubscriptionStatus(status: Subscription['status']) {
  if (status === 'active') return 'Active'
  if (status === 'past_due') return 'Past Due'
  return status.charAt(0).toUpperCase() + status.slice(1).replaceAll('_', ' ')
}
